import * as models from 'smg/customer-api/models';
import { CanvasViewport, CustomizerEngine, type ViewportState } from 'smg/customizer';

import { WORKING_DPI } from './geometry';
import { OpenSizeCheck } from './size-check';

// Zoom levels the step controls visit, as multiples of actual size: whole steps, then
// the point where the pin fills the stage when that comes first, so the whole pin is
// always in view
const ZOOM_STOPS = [1, 2, 3, 4, 5, 6];
// Share of the stage the pin fills along its tighter axis at its largest (and at its
// default, for a pin too big for the stage at actual size)
const FIT_FILL = 0.86;
// Zooms this close together count as the same zoom
const ZOOM_EPSILON = 0.005;
// A browser's nominal inch — the actual-size estimate until the screen has been matched
// to a real object (it measures ~0.6" on phones, ~0.9" on desktop monitors)
const CSS_PX_PER_INCH = 96;
// How long the first-zoom hint stays up
const HINT_MS = 4200;
// Ruler ticks stay at least this many px apart
const RULER_MIN_TICK_PX = 5;
// The matched screen resolution, per browser (it describes this screen, not the design)
const PX_PER_INCH_STORAGE_KEY = 'pin-builder-screen-px-per-inch';
// The stage keeps a band along its bottom clear for the zoom controls' row (inset + row
// height + gap: 44 px buttons from lg, 36 px compact ones below), so no pin at any zoom
// runs under them; from lg an empty pin also leaves room under it for its Upload · Add
// Text row (44 px + 12 px gap)
const WIDE_QUERY = '(min-width: 992px)';
const CONTROLS_BAND_PX = 64;
const CONTROLS_BAND_PX_COMPACT = 52;
const STARTER_ROW_PX = 56;

// What the preview shows the pin at: its actual size (100%), larger (zoomed in), or
// smaller because it is bigger than the stage (fitted)
type scaleView = 'actual' | 'zoomed' | 'fitted';

function scaleViewOf(zoom: number): scaleView {
    if (zoom > 1 + ZOOM_EPSILON) {
        return 'zoomed';
    }
    if (zoom < 1 - ZOOM_EPSILON) {
        return 'fitted';
    }
    return 'actual';
}

/**
 * The pin builder's zoomable stage: every size drawn at its actual size, the zoom
 * controls, the inch ruler that follows the view, the first-zoom hint, the scale note,
 * the empty pin's upload target, and the size check that makes actual size
 * exact on this screen. The page attaches each engine it builds; the view resets to its
 * default (100%, or fitted for a pin too big for the stage) each time.
 */
export class PinStageView {
    #viewport: CanvasViewport;
    #engine: CustomizerEngine | null = null;
    #size: models.LineItemTypeSize | null = null;
    #hintShown = false;
    #empty = true;
    #wide = window.matchMedia(WIDE_QUERY);
    // The customer's screen match, or null while actual size is the browser's estimate
    #pxPerInch: number | null = readPxPerInch();

    #$controls = document.getElementById('stage-view-controls');
    #$zoomIn = document.getElementById('stage-view-zoom-in');
    #$zoomOut = document.getElementById('stage-view-zoom-out');
    #$zoomReset = document.getElementById('stage-view-zoom-reset');
    #$readout = document.getElementById('stage-view-zoom-readout');
    #$status = document.getElementById('stage-view-zoom-status');
    #$hint = document.getElementById('stage-view-hint');
    #$ruler = document.getElementById('stage-view-ruler');
    // The scale note renders twice (under the pin at lg+, under the sticky preview below)
    #$sizeNames = document.querySelectorAll('.stage-view-size-name');
    #$scalePercents = document.querySelectorAll('.stage-view-scale-percent');
    // Scale note states, each shown for the view it names
    #$scaleParts = document.querySelectorAll<HTMLElement>('[data-scale-views]');
    #scaleView: scaleView = 'actual';

    constructor() {
        this.#viewport = new CanvasViewport({
            pxPerInch: this.#pxPerInch ?? CSS_PX_PER_INCH,
            unitsPerInch: WORKING_DPI,
            fill: FIT_FILL,
            stops: ZOOM_STOPS,
        });
        this.#viewport.onChange(state => this.#render(state));

        this.#$zoomIn?.addEventListener('click', () => this.#viewport.zoomIn());
        this.#$zoomOut?.addEventListener('click', () => this.#viewport.zoomOut());
        this.#$zoomReset?.addEventListener('click', () => this.#viewport.reset());
        document.querySelectorAll('.stage-view-size-check-btn').forEach($btn => {
            $btn.addEventListener('click', () => void this.#checkSize());
        });
        document.querySelectorAll('.stage-view-show-actual-btn').forEach($btn => {
            $btn.addEventListener('click', () => this.#viewport.reset());
        });
        this.#renderScaleNote();
        this.#wide.addEventListener('change', () => this.#applyInset());
        this.#applyInset();
    }

    /** The pin went from empty to designed or back — an empty pin leaves room for its row */
    setEmpty(empty: boolean): void {
        this.#empty = empty;
        this.#applyInset();
    }

    #applyInset(): void {
        let inset = CONTROLS_BAND_PX_COMPACT;
        if (this.#wide.matches) {
            inset = CONTROLS_BAND_PX;
            if (this.#empty) {
                inset += STARTER_ROW_PX;
            }
        }
        this.#viewport.setInsetBottom(inset);
    }

    /** Show a freshly built engine at the default view */
    attach(engine: CustomizerEngine, sizeInit: models.ILineItemTypeSize): void {
        this.#engine = engine;
        this.#size = new models.LineItemTypeSize(sizeInit);
        this.#viewport.attach(engine);
        this.#renderScaleNote();
    }

    /** The stage's box changed (layout settled, card heights equalized) */
    refresh(): void {
        this.#viewport.refresh();
    }

    #render(state: ViewportState): void {
        const percent = `${Math.round(state.zoom * 100)}%`;
        if (this.#$readout) {
            this.#$readout.textContent = percent;
        }
        if (this.#$status) {
            this.#$status.textContent = `Current zoom is ${percent}.`;
        }
        this.#$zoomIn?.toggleAttribute('disabled', state.zoom >= state.maxZoom - ZOOM_EPSILON);
        this.#$zoomOut?.toggleAttribute('disabled', state.zoom <= state.minZoom + ZOOM_EPSILON);
        this.#$zoomReset?.classList.toggle('d-none', Math.abs(state.zoom - state.defaultZoom) < ZOOM_EPSILON);
        // A pin already as large as the stage allows (a big pin on a phone) can't zoom
        // at all — no controls for it
        const canZoom = state.maxZoom - state.minZoom > ZOOM_EPSILON;
        this.#$controls?.classList.toggle('d-none', !canZoom);
        if (!canZoom) {
            this.#$hint?.classList.add('d-none');
        }

        // Where the pin sits in the stage box, for parts laid over it (the empty pin's
        // upload target) — the stage's top-left is the box's
        const $box = this.#engine?.getStage().container().parentElement;
        if ($box && this.#size) {
            const width = state.designWidth * state.scale;
            const height = state.designHeight * state.scale;
            $box.style.setProperty('--stage-view-pin-left', `${state.x}px`);
            $box.style.setProperty('--stage-view-pin-top', `${state.y}px`);
            $box.style.setProperty('--stage-view-pin-width', `${width}px`);
            $box.style.setProperty('--stage-view-pin-height', `${height}px`);
            $box.style.setProperty('--stage-view-pin-short', `${Math.min(width, height)}px`);
            $box.dataset.pinShape = this.#size.type;
        }

        if (state.zoom > 1.01) {
            this.#showHint();
        }
        this.#$scalePercents.forEach($percent => {
            $percent.textContent = percent;
        });
        this.#scaleView = scaleViewOf(state.zoom);
        this.#renderScaleNote();
        this.#renderRuler(state);
    }

    // The note says what the preview shows: only at 100% is that the pin's size
    #renderScaleNote(): void {
        const name = this.#size?.name ?? '';
        this.#$sizeNames.forEach($name => {
            $name.textContent = name;
        });
        const checked = String(this.#pxPerInch !== null);
        this.#$scaleParts.forEach($part => {
            const views = $part.dataset.scaleViews?.split(' ') ?? [];
            const shown = views.includes(this.#scaleView) && ($part.dataset.scaleChecked === undefined || $part.dataset.scaleChecked === checked);
            // Invisible, not removed: every state keeps its place, so the note's height
            // (and the stage's above it) never changes as the state does
            $part.classList.toggle('invisible', !shown);
            $part.toggleAttribute('aria-hidden', !shown);
        });
    }

    async #checkSize(): Promise<void> {
        const pxPerInch = await OpenSizeCheck(this.#pxPerInch ?? CSS_PX_PER_INCH);
        if (pxPerInch === null) {
            return;
        }
        this.#pxPerInch = pxPerInch;
        writePxPerInch(pxPerInch);
        this.#viewport.setPxPerInch(pxPerInch);
        // Back to the default — actual size is what the customer just measured
        this.#viewport.reset();
        this.#renderScaleNote();
    }

    #showHint(): void {
        if (this.#hintShown || !this.#$hint) {
            return;
        }
        this.#hintShown = true;
        this.#$hint.classList.remove('d-none');
        setTimeout(() => this.#$hint?.classList.add('d-none'), HINT_MS);
    }

    // Inch ruler under the stage, zero at the pin's left edge, the pin's width shaded;
    // it follows the zoom
    #renderRuler(state: ViewportState): void {
        const $ruler = this.#$ruler;
        if (!$ruler) {
            return;
        }
        const svgNS = 'http://www.w3.org/2000/svg';
        const pxPerInch = state.scale * WORKING_DPI;
        const width = $ruler.clientWidth || state.stageWidth;
        const height = $ruler.clientHeight;
        let stepIn = 0.5;
        if (pxPerInch / 8 >= RULER_MIN_TICK_PX) {
            stepIn = 0.125;
        } else if (pxPerInch / 4 >= RULER_MIN_TICK_PX) {
            stepIn = 0.25;
        }

        const $band = document.createElementNS(svgNS, 'rect');
        $band.classList.add('stage-view-ruler-band');
        $band.setAttribute('x', String(state.x));
        $band.setAttribute('y', '0');
        $band.setAttribute('width', String(state.designWidth * state.scale));
        $band.setAttribute('height', String(height));
        const parts: SVGElement[] = [$band];

        const first = Math.floor(-state.x / pxPerInch / stepIn);
        const last = Math.ceil((width - state.x) / pxPerInch / stepIn);
        for (let i = first; i <= last; i++) {
            const inches = i * stepIn;
            const x = state.x + inches * pxPerInch;
            // Binary fractions of an inch are exact in floating point
            const whole = Number.isInteger(inches);
            let length = 3;
            if (whole) {
                length = 11;
            } else if (Number.isInteger(inches * 2)) {
                length = 8;
            } else if (Number.isInteger(inches * 4)) {
                length = 5;
            }
            const $tick = document.createElementNS(svgNS, 'line');
            $tick.setAttribute('x1', String(x));
            $tick.setAttribute('x2', String(x));
            $tick.setAttribute('y1', '0');
            $tick.setAttribute('y2', String(length));
            parts.push($tick);
            if (whole && inches >= 0) {
                const $label = document.createElementNS(svgNS, 'text');
                $label.setAttribute('x', String(x + 3));
                $label.setAttribute('y', String(height - 4));
                if (inches === 0) {
                    $label.textContent = '0';
                } else {
                    $label.textContent = `${inches}"`;
                }
                parts.push($label);
            }
        }
        $ruler.replaceChildren(...parts);
    }
}

function readPxPerInch(): number | null {
    try {
        const value = parseFloat(localStorage.getItem(PX_PER_INCH_STORAGE_KEY) ?? '');
        if (Number.isFinite(value) && value > 0) {
            return value;
        }
    } catch {
        // Storage blocked — the check can still run, it just won't be remembered
    }
    return null;
}

function writePxPerInch(pxPerInch: number): void {
    try {
        localStorage.setItem(PX_PER_INCH_STORAGE_KEY, String(pxPerInch));
    } catch {
        // Storage blocked — the match lasts for this page view
    }
}
